import * as commonPcApi from "@/api/commonPc3341";
import {
  EnvMap,
  OBS_POSTER_SUFFIX,
  REGEXP_ORIGIN_YISHOU,
  UPLOAD_DIR,
  VIDEO_EXTENSION_LIST,
} from "@/constant";
import type { MediaFile, OBSUploadData } from "@/types";
import { generateImageToUploadFile } from "@/utils/help";
import type { YsFileResult, YsUploadFile, CodecInfo } from "@/utils/types";
import { SizeUnit } from "@/utils/types";
import { request } from "@umijs/max";
import type { UploadFile } from "antd";
import Md5 from "crypto-js/md5";
import domtoimage from "dom-to-image";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import * as MP4Box from "mp4box";
import { getLocalEnvStorage } from "@/utils/storage";
import {
  FileExportSystem,
  FileUploadCdnDomain,
  FileUploadDomain,
} from "@/constant/file";

const isProd = REACT_APP_ENV === "prod";

/**
 * 上传文件到obs
 */
export const uploadBlob = async (
  blob: Blob,
  obsUploadData: OBSUploadData,
): Promise<{ name: string; url: string }> => {
  const random = Md5(`${blob.type}${Date.now()}`).toString();
  const suffix = blob.type.split("/").splice(-1);
  const key = `${UPLOAD_DIR}${dayjs().format("YYYY/MM/DD/")}${random}.${suffix}`;
  const formData = new FormData();
  if (obsUploadData) {
    Object.keys(obsUploadData).forEach((keyName) => {
      formData.append(keyName, obsUploadData[keyName as keyof OBSUploadData]);
      return null; // Add a return statement here
    });
    formData.append("key", key);
    formData.append("file", blob);

    const originAction = obsUploadData.domain as string;
    /**
     * 直接用华为云的域名，不用 cdn 域名（img4 是cdn域名）
     */
    const url = originAction.includes(FileUploadCdnDomain)
      ? FileUploadDomain
      : originAction;
    await request(url, {
      method: "post",
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
      withCredentials: false,
    });
    return { name: key, url: `${obsUploadData.domain}${key}` };
  } else {
    throw new Error(`文件上传参数错误`);
  }
};

/**
 * 下载商城二进制文件
 */
export const fileDownload = async (res: YsFileResult): Promise<void> => {
  console.log("res", res);
  const file = typeof res === "string" ? res : res.file || "";
  console.log("file", file);
  return commonPcApi
    .baobanPcExport({ file }, { responseType: "blob" })
    .then((result) => {
      const blob = new Blob([result as unknown as BlobPart]);
      saveAs(blob, file);
    });
};

/**
 * 获取当前的下载域名
 * @param system 系统
 */
export const getDownloadHost = (system: FileExportSystem) => {
  const {
    erp,
    erpHost = "",
    erpSuffix = EnvMap.erp.suffix[0],
    os,
    osHost = "",
    osSuffix = EnvMap.os.suffix[0],
  } = getLocalEnvStorage();
  const hostMap = {
    [FileExportSystem.OS]: isProd
      ? "https://api.yishouapp.com"
      : `${osHost}${os || ""}${osSuffix}`,
    [FileExportSystem.ERP]: isProd
      ? "https://erpapi.yishouapp.com"
      : `${erpHost}${erp || ""}${erpSuffix}`,
  };
  return hostMap[system];
};

/**
 * 通过window open下载文件
 * @param res
 * @param system 系统，默认erp
 */
export const fileDownloadByWindowOpen = (
  res: YsFileResult,
  system: FileExportSystem = FileExportSystem.ERP,
): void => {
  const file = typeof res === "string" ? res : res.file || "";
  // 生产环境将 http:// 替换为 https:// 以确保下载正常
  const fileUrl = isProd ? file.replace(/^http:\/\//, "https://") : file;
  window.open(
    `${/^http/.test(fileUrl) ? "" : getDownloadHost(system)}${fileUrl}`,
  );
};

/**
 * 设置上传文件地址
 * @param file
 * @param obsUploadData
 */
export const setFileUploadUrl = (
  file: MediaFile,
  obsUploadData: OBSUploadData,
): MediaFile => {
  const suffix = file.name.slice(file.name.lastIndexOf("."));
  const random = Md5(`${Date.now()}${file.name}`).toString();
  const url = `${UPLOAD_DIR}${dayjs().format("YYYY/MM/DD/")}${random}${suffix}`;
  file.url = url;
  file.wholeUrl = `${obsUploadData?.domain}${url}`;
  return file;
};

/**
 * 图片上传完后将thumbUrl替换
 */
export const setFileThumbUrl = (
  file: YsUploadFile<UploadFile>,
  obsUploadData?: OBSUploadData,
): YsUploadFile<UploadFile> => {
  if (file.status === "done") {
    const suffix = file.type?.replace(/^.*\//, "") ?? "";
    const videoSuffix = VIDEO_EXTENSION_LIST.includes(suffix)
      ? OBS_POSTER_SUFFIX
      : "";
    if (obsUploadData) {
      const hasOrigin = REGEXP_ORIGIN_YISHOU.exec(file.url ?? "") !== null;
      const wholeUrl = hasOrigin
        ? file.url
        : `${obsUploadData.domain}${file.url}`;
      file.thumbUrl = `${wholeUrl}${videoSuffix}`;
    } else {
      file.thumbUrl = `${file.wholeUrl}${videoSuffix}`;
    }
  }
  return file;
};

/**
 * 获取视频编码信息
 */
export const getVideoCodecInfo = (file: File): Promise<CodecInfo> => {
  return new Promise((resolve, reject) => {
    const mp4boxFile = MP4Box.createFile();
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = function (e) {
      const arrayBuffer = e.target?.result as ArrayBuffer;
      // @ts-ignore: 必须设置 fileStart：number 属性
      arrayBuffer.fileStart = 0;
      mp4boxFile.appendBuffer(arrayBuffer);
    };
    mp4boxFile.onReady = function (info: CodecInfo) {
      const durationActual = info.duration / info.timescale;
      resolve({
        ...info,
        durationActual,
        size: info.tracks.reduce((pre, cur) => pre + cur.size, 0),
      });
    };
    mp4boxFile.onError = function (info: CodecInfo) {
      reject(info);
    };
  });
};

/**
 * 判断浏览器是否支持视频格式
 */
export const isBrowserSupportCodec = (info: CodecInfo): boolean => {
  return info.mime.includes("video/mp4;") && !/hev|hvc/.test(info.mime);
};

/**
 * 判断视频大小
 */
export const getFileSize = (
  file: File,
  unit: SizeUnit = SizeUnit.mb,
): number => {
  const unitMap = {
    b: 1024 ** 0,
    kb: 1024 ** 1,
    mb: 1024 ** 2,
    gb: 1024 ** 3,
  };
  return Math.floor((file.size / unitMap[unit]) * 100) / 100;
};

/**
 * dom 生成图片并上传
 * @param id
 * @param obsUploadData
 */
export const generateDomToImage = (
  id: string,
  obsUploadData: OBSUploadData,
): Promise<string> => {
  if (!id) {
    throw new Error("请传入需要生成图片的dom元素id");
  }
  const node = document.getElementById(id);
  return domtoimage
    .toBlob(node)
    .then((blob: Blob) => uploadBlob(blob, obsUploadData))
    .then((res: Awaited<ReturnType<typeof uploadBlob>>) => res.name);
};

/**
 * 获取上传图片的宽高
 *  @param file
 */
export const getImageSize = (
  file: File,
): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const width = img.width;
          const height = img.height;
          resolve({ width, height });
        };
        img.src = e?.target?.result as string;
      };
      reader.readAsDataURL(file);
    } catch (e) {
      reject(e);
    }
  });
};

/**
 * 全链接图片
 */
export const getFileFullSrc = (
  src: string | undefined,
  obsUploadData: OBSUploadData,
): string => {
  if (!src) {
    return "";
  }
  const domain = obsUploadData?.domain as string;
  const reg = new RegExp(`^http`);
  const hasOrigin = reg.exec(src) !== null;
  return hasOrigin ? src : `${domain}${src}`;
};

/**
 * 转存第三方文件到obs
 * @param fileList
 * @param obsUploadData
 */
export const transferFileToObs = async (
  fileList: UploadFile[],
  obsUploadData: OBSUploadData,
): Promise<UploadFile[]> => {
  const result: UploadFile[] = [];
  for (let i = 0; i < fileList.length; i++) {
    const item = fileList[i];
    if (item && item?.thumbUrl) {
      try {
        const { url } = await commonPcApi.baobanFileUpload({
          url: item.thumbUrl,
        });
        result.push(generateImageToUploadFile(url, obsUploadData)[0]);
      } catch (e) {
        result.push({
          ...item,
          status: "error",
        });
      }
    } else {
      result.push(item);
    }
  }
  return Promise.resolve(result);
};

/**
 * 获取视频链接封面
 */
export const getVideoPoster = (videoUrl: string): string => {
  if (!videoUrl || videoUrl.includes(OBS_POSTER_SUFFIX)) {
    return videoUrl;
  }
  // 获取文件路径后缀
  const suffix = videoUrl.replace(/\?.*/, "").replace(/^.*\./, "") ?? "";
  const videoSuffix = VIDEO_EXTENSION_LIST.includes(suffix)
    ? OBS_POSTER_SUFFIX
    : "";
  return `${videoUrl}${videoSuffix}`;
};
